/**
 * De Anza College brand colors (W48 Worker A, S3 step 4 — theme).
 *
 * Source (first-party, text-citable): the college's own stylesheets, fetched
 * 2026-09-05 via mobile.deanza.edu —
 *   /_resources/css/home.css declares `--primary: #8b032c`,
 *   `--primary-dark: #6a0221`, `--accent: #f0c230`, `--text-dark: #0f1419`;
 *   /_resources/stylesheets/screen.css uses #8b032c 162× and #f0c230 105×
 *   (next most common hues are neutral greys); the gold text tier the site
 *   itself uses is #9f7012 (6×). The official "Logos, Mascot and Colors"
 *   package is a zip on deanza.edu/communications/logos.html (binary, not the
 *   citation). Colors: De Anza Maroon + Gold.
 *
 * Contrast (WCAG, measured 2026-09-06): Maroon #8B032C on white 9.78:1 (AA,
 * stays the primary DEFAULT); Dark Maroon #6A0221 12.78:1 is the `-strong`
 * tier. The site's golds fail as TEXT on light surfaces — bright #F0C230 is
 * 1.68:1 on white and even the site's own text gold #9F7012 is only 3.89:1 on
 * the warm paper #F5F1EB / 4.37:1 on the white card — and `text-accent` DOES
 * render at body size in the audit (RequirementNode override badge,
 * RequirementOverrideControl pill, RequirementProgressBar "via override" label:
 * text-xs font-medium, 12 px), so the 4.5:1 floor applies (PR #314 review).
 * The accent TOKEN is therefore the gold hue darkened until it clears 4.5 on
 * BOTH light surfaces: #916610 = 4.54:1 on paper #F5F1EB, 5.11:1 on the card.
 * Dark appearance: the theme system exposes no dark-mode accent token (the
 * same limitation SDSU recorded), so this value renders on the dark surfaces
 * at 3.60:1 (#161412) / 3.32:1 (#1F1C19) — the 3:1 UI/large-text floor, not
 * 4.5; a per-theme accent-dm variant is the durable fix (the bright gold would
 * be 10.9:1 there). The bright gold lives in the -200/-300 tints for fills.
 *
 * Brand mark (components/ui/BrandMark.tsx cross-theme distance ≥ 50): plain
 * Maroon 139 3 44 vs SDSU's light front (Dark Red 166 25 46) is only ≈35, so
 * the LIGHT front face is Dark Maroon 106 2 33 (65.6 from SDSU, 133.8 from
 * UMD); the DARK front face is Maroon 139 3 44 (76.3 from SDSU dm, 173.3 from
 * UMD dm). Ramps are HSL interpolations around the brand values (De Anza
 * publishes no extended palette). Text-only branding, no logo asset embedded.
 */
import type { ThemeColors } from '../types';

export const deanzaColors: ThemeColors = {
  // Primary — De Anza Maroon (#8B032C = rgb(139, 3, 44)); -strong = Dark Maroon #6A0221.
  '--color-primary':     '139 3 44',
  '--color-primary-strong': '106 2 33',
  '--color-primary-50': '255 240 244',
  '--color-primary-100': '254 220 230',
  '--color-primary-200': '253 180 202',
  '--color-primary-300': '252 115 156',
  '--color-primary-400': '250 30 97',
  '--color-primary-500': '139 3 44',
  '--color-primary-600': '120 3 38',
  '--color-primary-700': '100 2 32',
  '--color-primary-800': '80 2 25',
  '--color-primary-900': '60 1 19',

  // Accent — De Anza gold, light-mode TEXT tier (#916610 = rgb(145, 102, 16)); see header.
  '--color-accent':     '145 102 16',
  '--color-accent-50': '253 248 237',
  '--color-accent-100': '250 236 209',
  '--color-accent-200': '245 218 163',
  '--color-accent-300': '238 191 99',
  '--color-accent-400': '230 162 25',
  '--color-accent-500': '145 102 16',
  '--color-accent-600': '124 87 14',
  '--color-accent-700': '101 71 11',
  '--color-accent-800': '78 55 9',
  '--color-accent-900': '55 39 6',

  // Brand-mark faces. Light: Dark Maroon front (identity-safe vs SDSU), two darker
  // oblique steps. Dark: Maroon front stepping down through Dark Maroon.
  '--color-mark-front': '106 2 33',
  '--color-mark-top':   '82 2 26',
  '--color-mark-side':  '58 1 18',
  '--color-mark-front-dm': '139 3 44',
  '--color-mark-top-dm':   '106 2 33',
  '--color-mark-side-dm':  '78 2 24',
};
